/**
 * CSS colour literals a design-guide table is full of: `#fff`, `#FDFBF7`, `#RRGGBBAA`,
 * `rgb()`/`rgba()`, `hsl()`/`hsla()`, and the modern space-separated forms.
 *
 * Anchored, so it only ever matches a code span that is *entirely* a colour — `#FDFBF7` gets a
 * swatch, a sentence mentioning one does not.
 */
const COLOR_PATTERN = /^(#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})|(?:rgba?|hsla?)\([^)]*\))$/i;

export function isColorLiteral(text: string): boolean {
  return COLOR_PATTERN.test(text.trim());
}

/**
 * A colour chip rendered beside the literal that produced it, so a palette table reads as
 * colours rather than as hex strings the reader has to imagine.
 *
 * The chip sits on a checkerboard so a translucent value (`rgba(46, 46, 46, 0.08)` — exactly what
 * a shadow token looks like) is visibly translucent instead of reading as "nearly white".
 */
export function ColorSwatch({ value }: { value: string }) {
  return (
    <span
      aria-hidden
      className="inline-block size-3 shrink-0 overflow-hidden rounded-full border border-line align-[-0.125em]"
      style={{
        backgroundImage:
          'linear-gradient(45deg, var(--color-line) 25%, transparent 25%, transparent 75%, var(--color-line) 75%), linear-gradient(45deg, var(--color-line) 25%, transparent 25%, transparent 75%, var(--color-line) 75%)',
        backgroundSize: '6px 6px',
        backgroundPosition: '0 0, 3px 3px',
      }}
    >
      <span className="block size-full" style={{ backgroundColor: value }} />
    </span>
  );
}
